/* Stompies add-to-basket feedback: haptics on phones and a soft two-note chime.
   - Press any add-to-basket button: a light tap (phones).
   - Basket confirms: a short double pulse (phones) and the chime (all devices).
   - Add fails: a firmer buzz (phones), no chime.
   Android uses the Vibration API. iOS Safari has no Vibration API, but toggling a hidden
   switch control (Safari 18+) plays the system's light tap.
   The chime is synthesised with Web Audio (no file to download), kept quiet, and uses the
   "ambient" audio session where supported so it obeys the silent switch and never stops music. */

const touchPhone = matchMedia('(hover: none) and (pointer: coarse)');
const ADD_BUTTONS = 'add-to-cart-component button, [ref="addToCartButton"], .stp-size, .stp-sheet__add';

const patterns = { tick: 4, tap: 8, success: [12, 70, 20], error: [35, 60, 35] };

function iosTick() {
  const label = document.createElement('label');
  label.setAttribute('aria-hidden', 'true');
  label.style.display = 'none';
  const input = document.createElement('input');
  input.type = 'checkbox';
  input.setAttribute('switch', '');
  label.appendChild(input);
  document.head.appendChild(label);
  label.click();
  label.remove();
}

export function haptic(kind = 'tap') {
  if (!touchPhone.matches) return;
  if (typeof navigator.vibrate === 'function') {
    try { navigator.vibrate(patterns[kind] || patterns.tap); } catch (_) { /* blocked without a recent tap */ }
    return;
  }
  iosTick();
  if (kind === 'success' || kind === 'error') setTimeout(iosTick, kind === 'error' ? 140 : 90);
}
/* Shared with the Pick a pal slider, which ticks as each pair lines up */
window.stompiesHaptic = haptic;

/* ---------- Swipe ticks ----------
   A light tick for each photo or pair that passes when someone swipes the product photos or the
   Pick a pal row. Android ticks as each one lines up. iOS only plays the tick inside a tap or as a
   finger lifts, so there it ticks once as each swipe lets go. The listeners sit on the document
   because Horizon swaps the product gallery out whenever the animal changes. */
const SWIPERS = '.product-information__media slideshow-slides, .stp-gang__track';
const canVibrate = typeof navigator.vibrate === 'function';
const swipe = { el: null, x: 0, y: 0, index: 0, until: 0 };

function itemIndex(scroller) {
  const items = scroller.children;
  if (items.length < 2) return 0;
  const step = items[1].offsetLeft - items[0].offsetLeft || scroller.clientWidth || 1;
  return Math.round(scroller.scrollLeft / step);
}

document.addEventListener('touchstart', (event) => {
  const el = event.target instanceof Element ? event.target.closest(SWIPERS) : null;
  swipe.el = el;
  if (!el) return;
  swipe.x = event.touches[0].clientX;
  swipe.y = event.touches[0].clientY;
  swipe.index = itemIndex(el);
  swipe.until = Infinity;
}, { passive: true, capture: true });

document.addEventListener('touchend', (event) => {
  const el = swipe.el;
  if (!el) return;
  swipe.until = performance.now() + 900; // the row keeps gliding for a moment after the finger lifts
  if (canVibrate) return;
  const dx = event.changedTouches[0].clientX - swipe.x;
  const dy = event.changedTouches[0].clientY - swipe.y;
  if (Math.abs(dx) < 30 || Math.abs(dx) < Math.abs(dy)) return;
  const atStart = el.scrollLeft <= 1;
  const atEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 1;
  if ((dx > 0 && atStart && swipe.index === 0) || (dx < 0 && atEnd)) return; // pushing past either end
  haptic('tick');
}, { passive: true, capture: true });

document.addEventListener('scroll', (event) => {
  if (!canVibrate || event.target !== swipe.el || performance.now() > swipe.until) return;
  const i = itemIndex(swipe.el);
  if (i === swipe.index) return;
  swipe.index = i;
  haptic('tick');
}, { passive: true, capture: true });

/* ---------- Chime ---------- */
let audio = null;

/* Must run inside the tap itself: browsers only let a page start audio after a user gesture */
function unlockAudio() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return;
  try {
    if ('audioSession' in navigator) navigator.audioSession.type = 'ambient';
    if (!audio) audio = new Ctx();
    if (audio.state === 'suspended') audio.resume();
  } catch (_) { audio = null; }
}

/* A small bell: each note is a sine plus a quiet inharmonic partial, with a fast attack and soft decay */
function bell(freq, start, level) {
  const out = audio.createGain();
  out.gain.setValueAtTime(0.0001, start);
  out.gain.exponentialRampToValueAtTime(level, start + 0.008);
  out.gain.exponentialRampToValueAtTime(0.0001, start + 0.55);
  out.connect(audio.destination);
  [[1, 1], [2.76, 0.18]].forEach(([ratio, mix]) => {
    const osc = audio.createOscillator();
    const g = audio.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq * ratio;
    g.gain.value = mix;
    osc.connect(g).connect(out);
    osc.start(start);
    osc.stop(start + 0.6);
  });
}

export function chime() {
  if (!audio || audio.state !== 'running') return;
  const now = audio.currentTime + 0.01;
  bell(1046.5, now, 0.07);        // C6
  bell(1568.0, now + 0.09, 0.06); // G6
}

/* Tap the moment an add button is pressed (still inside the user's gesture, which iOS needs) */
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest(ADD_BUTTONS) : null;
  if (!button || button.disabled) return;
  unlockAudio();
  haptic('tap');
}, { capture: true });

/* Pulse and chime once the basket confirms. Horizon's product form and the Stompies quick add
   both announce adds with Shopify's cart lines event. */
try {
  const { StandardEvents } = await import('@shopify/events');
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (event.action !== 'add' || !event.promise) return;
    event.promise
      .then(({ detail } = {}) => {
        if (detail && detail.didError) { haptic('error'); return; }
        haptic('success');
        chime();
      })
      .catch((error) => { if (!error || error.name !== 'AbortError') haptic('error'); });
  });
} catch (_) { /* no cart events on this page: the tap on press still plays */ }
